import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const SOFT_ORB = 'radial-gradient(circle, rgba(148, 163, 184, 0.14), transparent 70%)';
const PARTICLE_COLOR = 'rgba(148, 163, 184, 0.45)';
const RING_COLOR = 'rgba(148, 163, 184, 0.18)';
const LINE_GRADIENT =
  'linear-gradient(to right, transparent, rgba(148, 163, 184, 0.25), transparent)';
const TRIANGLE_COLOR = 'rgba(148, 163, 184, 0.22)';

const FloatingElements: React.FC = () => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return null;

  const orbs = [
    { left: '15%', top: '18%', size: 280, duration: 12, delay: 0 },
    { left: '70%', top: '60%', size: 340, duration: 14, delay: 2 },
    { left: '45%', top: '82%', size: 220, duration: 16, delay: 1 },
    { left: '82%', top: '12%', size: 200, duration: 13, delay: 3 },
    { left: '8%', top: '70%', size: 240, duration: 15, delay: 1.5 },
  ];

  const particles = [...Array(14)].map((_, i) => ({
    left: `${6 + i * 6.5}%`,
    top: `${10 + (i % 5) * 18}%`,
    duration: 6 + (i % 4) * 1.5,
    delay: i * 0.6,
  }));

  const rings = [
    { left: '25%', top: '55%', size: 90, duration: 9, delay: 0 },
    { left: '72%', top: '28%', size: 70, duration: 11, delay: 1.5 },
    { left: '55%', top: '75%', size: 110, duration: 12, delay: 2.5 },
    { left: '15%', top: '85%', size: 60, duration: 10, delay: 0.8 },
  ];

  const lines = [
    { left: '18%', top: '38%', width: 120, duration: 8, delay: 0 },
    { left: '55%', top: '68%', width: 160, duration: 10, delay: 2 },
    { left: '30%', top: '15%', width: 100, duration: 9, delay: 1 },
    { left: '65%', top: '45%', width: 140, duration: 11, delay: 3 },
  ];

  const triangles = [
    { left: '22%', top: '30%', size: 12, duration: 11, delay: 0 },
    { left: '68%', top: '20%', size: 14, duration: 13, delay: 1.5 },
    { left: '40%', top: '55%', size: 10, duration: 10, delay: 0.8 },
    { left: '85%', top: '50%', size: 13, duration: 12, delay: 2.2 },
    { left: '12%', top: '60%', size: 11, duration: 14, delay: 1 },
  ];

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      {orbs.map((orb, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute rounded-full blur-2xl"
          style={{
            left: orb.left,
            top: orb.top,
            width: orb.size,
            height: orb.size,
            background: SOFT_ORB,
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: orb.duration,
            repeat: Infinity,
            delay: orb.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {rings.map((ring, i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute rounded-full"
          style={{
            left: ring.left,
            top: ring.top,
            width: ring.size,
            height: ring.size,
            border: `1px solid ${RING_COLOR}`,
          }}
          animate={{
            scale: [0.9, 1.15, 0.9],
            opacity: [0.3, 0.55, 0.3],
          }}
          transition={{
            duration: ring.duration,
            repeat: Infinity,
            delay: ring.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {particles.map((particle, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: particle.left,
            top: particle.top,
            background: PARTICLE_COLOR,
          }}
          animate={{
            y: [0, -50, 0],
            opacity: [0, 0.7, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {triangles.map((tri, i) => (
        <motion.div
          key={`triangle-${i}`}
          className="absolute"
          style={{
            left: tri.left,
            top: tri.top,
            width: 0,
            height: 0,
            borderLeft: `${tri.size}px solid transparent`,
            borderRight: `${tri.size}px solid transparent`,
            borderBottom: `${tri.size * 1.6}px solid ${TRIANGLE_COLOR}`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.25, 0.6, 0.25],
            rotate: [0, 8, 0],
          }}
          transition={{
            duration: tri.duration,
            repeat: Infinity,
            delay: tri.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {lines.map((line, i) => (
        <motion.div
          key={`line-${i}`}
          className="absolute h-px"
          style={{
            left: line.left,
            top: line.top,
            width: line.width,
            background: LINE_GRADIENT,
          }}
          animate={{
            opacity: [0, 0.8, 0],
            scaleX: [0.6, 1, 0.6],
          }}
          transition={{
            duration: line.duration,
            repeat: Infinity,
            delay: line.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export default FloatingElements;
